// src/pages/donations/donationList.js
import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import dayjs from "dayjs";
import { Table, Pagination, Modal, DatePicker, Button, Tooltip } from "antd";
import { FaEdit, FaTrash } from "react-icons/fa";
import axios from "../../services/axiosConfig";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { getAuthToken } from "../../auth/auth";
import useNotification from "../../hooks/useNotification";
import Layout from "../../components/layout/layout";
import TableLayout from "../../components/tableLayout/tableLayout";
import TopToolBar from "../../components/topToolBar/topToolBar";
import "./donationList.css";

const { RangePicker } = DatePicker;

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  donations: [],
  currentPage: 1,
  pageSize: 10,
  sortConfig: { key: "createdAt", direction: "desc" },
  showDateRangeModal: false,
  tempFromDate: null,
  tempToDate: null,
  fromDate: null,
  toDate: null
};

// Reducer for state management
const donationReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setDonations":
      return { ...state, donations: action.payload };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload };
    case "setDateRangeModal":
      return { ...state, showDateRangeModal: action.payload };
    case "setTempDateRange":
      return {
        ...state,
        tempFromDate: action.payload.from,
        tempToDate: action.payload.to
      };
    case "setDateRange":
      return {
        ...state,
        fromDate: action.payload.from,
        toDate: action.payload.to
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1,
        fromDate: null,
        toDate: null,
        tempFromDate: null,
        tempToDate: null,
        showDateRangeModal: false
      };
    default:
      return state;
  }
};

function DonationList() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(donationReducer, initialState);

  const { isLoading, searchTerm, filters, donations, currentPage, pageSize, sortConfig, showDateRangeModal, tempFromDate, tempToDate, fromDate, toDate } = state;

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      paymentDate: {
        label: "Payment Date",
        options: [
          { value: "newest", label: "Newest" },
          { value: "last7Days", label: "Last 7 Days" },
          { value: "last30Days", label: "Last 30 Days" },
          { value: "oldest", label: "Oldest" },
          { value: "dateRange", label: "Custom Date Range" }
        ]
      },
      category: {
        label: "Category",
        options: [
          { value: "Offering", label: "Offering" },
          { value: "Tithe", label: "Tithe" },
          { value: "Event", label: "Event" },
          { value: "Ministry", label: "Ministry" },
          { value: "Other", label: "Other" }
        ]
      }
    }),
    []
  );

  // Date filtering utility
  const filterByDate = useCallback(
    (items, dateFilter) => {
      if (!dateFilter) return items;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (dateFilter) {
        case "newest":
          return [...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        case "oldest":
          return [...items].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        case "last7Days": {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 7);
          return items.filter((item) => new Date(item.createdAt) >= sevenDaysAgo);
        }
        case "last30Days": {
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          return items.filter((item) => new Date(item.createdAt) >= thirtyDaysAgo);
        }
        case "dateRange": {
          if (fromDate && toDate) {
            const from = new Date(fromDate);
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            return items.filter((item) => {
              const date = new Date(item.createdAt);
              return date >= from && date <= to;
            });
          }
          return items;
        }
        default:
          return items;
      }
    },
    [fromDate, toDate]
  );

  // Global search function
  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;

    const searchLower = term.toLowerCase().trim();

    return items.filter((item) => {
      const searchFields = [
        item.name || "",
        item.phoneNumber || "",
        item.category || "",
        item.currency || "",
        item.amount?.toString() || "",
        item.reminderSet ? "yes" : "no",
        item.createdAt
          ? new Date(item.createdAt)
              .toLocaleDateString("en-GB", {
                timeZone: "Asia/Kolkata",
                day: "2-digit",
                month: "short",
                year: "numeric"
              })
              .replace(/ /g, "-")
          : ""
      ];

      return searchFields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, []);

  // Apply all filters and search
  const filteredData = useMemo(() => {
    let result = [...donations];

    // Apply date filter first (includes sorting)
    result = filterByDate(result, filters.paymentDate);

    // Apply category filter
    if (filters.category) {
      result = result.filter((donation) => donation.category === filters.category);
    }

    // Apply global search
    result = globalSearch(result, searchTerm);

    // Apply sorting if no date filter is applied
    if (!filters.paymentDate && sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;
        if (key === "createdAt") {
          return direction === "asc" ? new Date(a.createdAt) - new Date(b.createdAt) : new Date(b.createdAt) - new Date(a.createdAt);
        }
        if (key === "amount") {
          return direction === "asc" ? a.amount - b.amount : b.amount - a.amount;
        }
        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [donations, filters, searchTerm, filterByDate, globalSearch, sortConfig]);

  // Get current page data
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Event handlers
  const handleSearch = useCallback((term) => {
    dispatch({ type: "setSearchTerm", payload: term });
  }, []);

  const handleFilterChange = useCallback(
    (newFilters) => {
      // Check if Custom Date Range was just selected
      if (newFilters.paymentDate === "dateRange" && filters.paymentDate !== "dateRange") {
        dispatch({ type: "setDateRangeModal", payload: true });
        return;
      }

      // Clear date range data if switching away from it
      if (filters.paymentDate === "dateRange" && newFilters.paymentDate !== "dateRange") {
        dispatch({
          type: "setDateRange",
          payload: { from: null, to: null }
        });
        dispatch({
          type: "setTempDateRange",
          payload: { from: null, to: null }
        });
      }

      dispatch({ type: "setFilters", payload: newFilters });
    },
    [filters]
  );

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  const handleEdit = useCallback(
    (record) => {
      console.log("Edit donation:", record);
      // Implement edit functionality
      showNotification(`Edit donation for ${record.name}`, "info");
    },
    [showNotification]
  );

  const handleDelete = useCallback(
    (record) => {
      console.log("Delete donation:", record);
      // Implement delete functionality
      if (window.confirm(`Are you sure you want to delete the donation from ${record.name}?`)) {
        // Add delete API call here
        showNotification(`Donation from ${record.name} deleted successfully`, "success");
      }
    },
    [showNotification]
  );

  const handleDateRangeChange = useCallback((dates, dateStrings) => {
    dispatch({
      type: "setTempDateRange",
      payload: {
        from: dateStrings[0] || null,
        to: dateStrings[1] || null
      }
    });
  }, []);

  const applyDateRangeFilter = useCallback(() => {
    if (tempFromDate && tempToDate) {
      dispatch({
        type: "setDateRange",
        payload: { from: tempFromDate, to: tempToDate }
      });
      dispatch({
        type: "setFilters",
        payload: { ...filters, paymentDate: "dateRange" }
      });
      dispatch({ type: "setDateRangeModal", payload: false });
    } else {
      alert("Please select both from and to dates");
    }
  }, [tempFromDate, tempToDate, filters]);

  const handleDateRangeModalCancel = useCallback(() => {
    dispatch({ type: "setDateRangeModal", payload: false });
    dispatch({
      type: "setTempDateRange",
      payload: { from: null, to: null }
    });
    dispatch({ type: "setFilters", payload: {} });
  }, []);

  // Table columns configuration
  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "NAME",
        dataIndex: "name",
        sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
        render: (name) => name || "-"
      },
      {
        title: "PHONE NUMBER",
        dataIndex: "phoneNumber",
        sorter: (a, b) => (a.phoneNumber || "").localeCompare(b.phoneNumber || ""),
        render: (phone) => phone || "-"
      },
      {
        title: "CATEGORY",
        dataIndex: "category",
        sorter: (a, b) => (a.category || "").localeCompare(b.category || ""),
        render: (category) => category || "-"
      },
      {
        title: "PAYMENT DATE & TIME",
        dataIndex: "createdAt",
        sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        render: (date) =>
          date
            ? new Date(date)
                .toLocaleString("en-GB", {
                  timeZone: "Asia/Kolkata",
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true
                })
                .replace(/,/g, " -")
            : "-"
      },
      {
        title: "CURRENCY",
        dataIndex: "currency",
        render: (currency) => (currency ? `${currency.toLocaleString()}` : "-")
      },
      {
        title: "AMOUNT",
        dataIndex: "amount",
        sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
        render: (amount) => (amount ? `${amount.toLocaleString()}` : "-")
      },
      {
        title: "REMINDER SET",
        dataIndex: "reminderSet",
        sorter: (a, b) => (a.reminderSet === b.reminderSet ? 0 : a.reminderSet ? -1 : 1),
        render: (reminderSet) => (
          <span
            style={{
              color: reminderSet ? "hsl(145, 60%, 40%)" : "hsl(0, 50%, 50%)",
              fontWeight: "500"
            }}
          >
            {reminderSet ? "Yes" : "No"}
          </span>
        )
      },
      {
        title: "ACTIONS",
        render: (_, record) => (
          <div className="dnl-action-buttons" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Edit">
              <button className="dnl-table-edit-btn" onClick={() => handleEdit(record)}>
                <FaEdit />
              </button>
            </Tooltip>
            <Tooltip title="Delete">
              <button className="dnl-table-delete-btn" onClick={() => handleDelete(record)}>
                <FaTrash />
              </button>
            </Tooltip>
          </div>
        ),
        width: 120
      }
    ],
    [currentPage, pageSize, handleEdit, handleDelete]
  );

  // Excel export data
  const prepareExcelData = useCallback(() => {
    return filteredData.map((donation, index) => ({
      "Sl. No.": index + 1,
      Name: donation.name,
      "Phone Number": donation.phoneNumber,
      Category: donation.category,
      Amount: donation.amount,
      "Reminder Set": donation.reminderSet ? "Yes" : "No",
      "Payment Date": donation.createdAt
        ? new Date(donation.createdAt)
            .toLocaleDateString("en-GB", {
              timeZone: "Asia/Kolkata",
              day: "2-digit",
              month: "short",
              year: "numeric"
            })
            .replace(/ /g, "-")
        : "-"
    }));
  }, [filteredData]);

  useEffect(() => {
    let isCancelled = false;

    async function fetchAdminDonations() {
      dispatch({ type: "setLoading", payload: true });

      try {
        // ---------------- Default date range (last 30 days) ----------------
        const defaultTo = new Date().toISOString();
        const defaultFrom = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString();

        // ---------------- Use selected range if available ----------------
        const fromDateIso = fromDate ? new Date(fromDate).toISOString() : defaultFrom;

        const toDateIso = toDate ? new Date(toDate).toISOString() : defaultTo;

        const token = await getAuthToken();

        const url = `${API_ENDPOINTS.DONATIONS_ADMIN}` + 
          `?fromDate=${encodeURIComponent(fromDateIso)}` + 
          `&toDate=${encodeURIComponent(toDateIso)}`;

        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });

        if (!res.ok) {
          const err = await res.text();
          throw new Error(err);
        }

        const data = await res.json();
        console.log("ADMIN DONATIONS API RESPONSE:", data);

        if (isCancelled) return;

        // ---------------- Normalize backend → UI model ----------------
        const items = (Array.isArray(data) ? data : []).map((d) => ({
          id: d.donationId,
          name: d.userName,
          phoneNumber: d.phoneNumber,
          category: d.donationType,
          createdAt: d.submittedAt,
          currency: "INR",
          amount: d.amount,
          reminderSet: Boolean(d.monthlyReminderDay)
        }));

        dispatch({
          type: "setDonations",
          payload: items
        });
      } catch (err) {
        if (!isCancelled) {
          console.error("GET /donations/admin error:", err);
          dispatch({
            type: "setDonations",
            payload: []
          });
        }
      } finally {
        if (!isCancelled) {
          dispatch({ type: "setLoading", payload: false });
        }
      }
    }

    fetchAdminDonations();

    return () => {
      isCancelled = true;
    };
  }, [fromDate, toDate]);

  return (
    <Layout
      navTitle="Donations"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search across all fields..."
            searchTerm={searchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="donation-list"
            excelSheetName="Donation Management"
          />
        }
      >
        <Table
          className="tpl-table"
          rowKey={(record) => record.id}
          rowClassName={(record, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
          expandable={{
            showExpandColumn: false
          }}
        />
        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      {/* Date Range Modal */}
      <Modal
        title="Select Date Range"
        open={showDateRangeModal}
        onCancel={handleDateRangeModalCancel}
        footer={[
          <Button key="cancel" onClick={handleDateRangeModalCancel}>
            Cancel
          </Button>,
          <Button key="apply" onClick={applyDateRangeFilter}>
            Apply Filter
          </Button>
        ]}
        width={450}
        centered={false}
        style={{ top: 120 }}
      >
        <div style={{ marginBottom: "20px" }}>
          <RangePicker
            style={{ width: "100%" }}
            value={tempFromDate && tempToDate ? [dayjs(tempFromDate), dayjs(tempToDate)] : []}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            placeholder={["From Date", "To Date"]}
          />
        </div>
      </Modal>
    </Layout>
  );
}

export default DonationList;
